import { ref, reactive } from 'vue'
import { getFetchCallBase, useFetch } from '@/composables/fetch'
import { isNumber } from 'lodash'
import type { GwTabsInfiniteProps } from '@/components/lib/GwTabs/GwTabsInfinite.vue'
import type { ApiError } from '../types/basic'

export type State<T, Params> = {
  data: T[]
  nextOffset?: number
  previousOffset?: number
  loading?: GwTabsInfiniteProps['loadingVariant']
  params?: Params
  limit?: number
  error?: ApiError
}

type FetchData<Params> = {
  offset?: number
  initial?: boolean
  isNext?: boolean
  limit?: number
  params?: Params
}

export const usePaginatedFetcher = <T, Params>(
  apiVersion: string,
  endpoint: string,
  initialData: T[] = [],
  initialLoading: GwTabsInfiniteProps['loadingVariant'] = undefined,
  updateCount?: (count: number) => void,
) => {
  const { fetch } = useFetch()

  const state = reactive<State<T, Params>>({
    data: initialData,
    loading: initialLoading,
  })

  const fetchDataCall = ref(
    getFetchCallBase({
      apiVersion,
      endpoint,
    }),
  )

  const fetchData = async ({
    offset = 0,
    initial = false,
    isNext = false,
    limit = state.limit,
    params,
  }: FetchData<Params>) => {
    fetchDataCall.value.params = {
      limit,
      offset,
      ...params,
    }
    if (initial) {
      state.limit = limit
    }

    state.loading = initial ? 'initial' : isNext ? 'right' : 'left'
    state.error = undefined

    if (fetchDataCall.value.loading) {
      return {
        loading: state.loading,
      }
    }
    const { data, error } = await fetch({
      ...fetchDataCall.value,
    })
    state.loading = undefined

    if (error) {
      if (error.canceled) return {}

      state.error = error
      return {}
    }

    if (initial || isNext || data.next) state.nextOffset = data.next ? offset + limit! : undefined

    if (initial || !isNext || data.previous)
      state.previousOffset = data.previous ? Math.max(offset - limit!, 0) : undefined

    if (initial) {
      state.data = data.results
    } else {
      state.data = isNext
        ? [...state.data, ...data.results]
        : [...data.results.reverse(), ...state.data]
    }

    if (updateCount) updateCount(data.count)

    return {
      data: state.data,
      nextOffset: state.nextOffset,
      previousOffset: state.previousOffset,
      loading: state.loading,
      count: data.count,
    }
  }

  const fetchNextData = async () => {
    if (state.nextOffset) {
      await fetchData({
        offset: state.nextOffset,
        isNext: true,
      })
    }
  }

  const fetchPreviousData = async () => {
    if (isNumber(state.previousOffset)) {
      await fetchData({
        offset: state.previousOffset,
      })
    }
    return true
  }

  return { fetchData, fetchNextData, fetchPreviousData, fetchDataCall, state }
}
